<template>
  <section class="overview-container">
    <t-floating-bar v-if="filter?.resource">
      {{ eventName(filter.resource) }}
    </t-floating-bar>
    <t-page-title title="Acessos | Borderô" :on-print="() => printingDialog = true">
      <template #prepend-custom-action>
        <v-btn
          v-if="can('update', 'readings')"
          v-tooltip="'Gerenciar Leituras de Sócios'"
          icon
          color="primary"
          :loading="loader.loading"
          class="no-print"
          @click="openSignatureReadingsDialog"
        >
          <img src="@/assets/images/logo_tdv_transparent.png" width="35">
        </v-btn>
      </template>
    </t-page-title>
    <v-form v-if="filter.resource" class="mb-8" @submit.prevent="fetch">
      <v-row>
        <v-col cols="12" class="only-print">
          <span class="text-h6 font-weight-bold">Evento: </span>
          <span class="text-h6">{{ eventName(filter.resource) }}</span>
        </v-col>
        <v-col
          :key="filter.field"
          cols="12"
          md="3"
          class="no-print"
        >
          <data-table-select-filter
            v-if="filter.type == 'select'"
            v-model="filter.model"
            :filter="filter"
            dense="compact"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-btn
            color="primary"
            :disabled="loader.loading"
            size="large"
            block
            type="submit"
            class="no-print"
          >
            Filtrar
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <v-alert
      v-if="data !== null && hasDiscounts"
      type="info"
      density="compact"
      variant="tonal"
      class="mb-4"
    >
      A receita apresentada já considera os descontos concedidos.
    </v-alert>
    <!-- Overview -->
    <overview-summary v-if="data !== null" :overview="data" />
    <!-- Sectors -->
    <overview-sectors v-if="data !== null" :overview="data" />
    <!-- Readings chart -->
    <overview-reading-charts
      v-if="data !== null"
      class="no-print"
      :overview="data"
    />

    <!-- Reservations report -->
    <overview-online-reservations v-if="data !== null" :overview="data" />

    <!-- Online sales report -->
    <overview-online-sales v-if="data !== null" :overview="data" />

    <!-- Box office sales report -->
    <overview-box-office v-if="data !== null" :overview="data" />

    <!-- Online courtesy report -->
    <overview-online-courtesies v-if="data !== null" :overview="data" />

    <!-- Box office courtesy report -->
    <overview-box-office-courtesies v-if="data !== null" :overview="data" />

    <!-- Free tickets report -->
    <overview-box-office-free-tickets v-if="data !== null" :overview="data" />

    <!-- Signature report -->
    <overview-signature-readings v-if="data !== null" :overview="data" />

    <!-- Action dialogs -->
    <dialog-signature-readings
      v-if="signatureReadingsDialog && filter.resource && data?.signatures"
      :readings="data.signatures"
      :event="filter.resource"
      @close="onSignatureReadingsDialogClose"
    />
    <dialog-printing
      v-if="printingDialog && filter.resource"
      :event="filter.resource"
      @close="printingDialog = false"
    />
  </section>
</template>

<script lang="ts" setup>
import { useProducerStore } from '@/stores/producer'
import { computed, onMounted, ref, Ref } from 'vue'
import TPageTitle from '@/components/TPageTitle.vue'
import TFloatingBar from '@/components/TFloatingBar.vue'
import EventService from '@/services/EventService'
import type Overview from '@/types/Overview'
import OverviewSummary from '@/components/events/overview/overview-summary.vue'
import OverviewSectors from '@/components/events/overview/overview-sectors.vue'
import OverviewBoxOffice from '@/components/events/overview/overview-box-office.vue'
import OverviewBoxOfficeCourtesies from '@/components/events/overview/overview-box-office-courtesies.vue'
import OverviewBoxOfficeFreeTickets from '@/components/events/overview/overview-box-office-free-tickets.vue'
import OverviewOnlineSales from '@/components/events/overview/overview-online-sales.vue'
import OverviewOnlineReservations from '@/components/events/overview/overview-online-reservations.vue'
import OverviewOnlineCourtesies from '@/components/events/overview/overview-online-courtesies.vue'
import OverviewSignatureReadings from '@/components/events/overview/overview-signature-readings.vue'
import OverviewReadingCharts from '@/components/events/overview/overview-reading-charts.vue'
import DataTableSelectFilter from '@/components/datatable/DataTableSelectFilter.vue'
import DialogSignatureReadings from './partials/dialog-signature-readings.vue'
import DialogPrinting from './partials/dialog-printing.vue'
import { useLoader } from '@/stores/loader'
import { useEventFilter } from '@/helpers/reports/eventFilter'
import { useSnackBar } from '@/stores/snackbar'
import { useRoute } from 'vue-router'
import { eventName } from '@/plugins/eventName'
import { useAbility } from '@casl/vue'
import { OverviewSignatureItem } from '@/types/Overview'
import { usePrintingStore } from '@/stores/printing'

const loader = useLoader()
const snackbar = useSnackBar()
const { can } = useAbility()

const filter = ref(
  useEventFilter({
    initial: useRoute().params.event !== '' ? useRoute().params.event as string : null,
    producer: useProducerStore().producer!,
    label: 'Evento',
    field: 'event_id',
    type: 'select',
  }),
)

const data: Ref<Overview | null> = ref(null)

const hasDiscounts = computed(() => (data.value?.summary ?? []).some(item => parseFloat(item.discounts) > 0))

async function fetch() {
  try {
    loader.increment()

    await filter.value.ready

    const id = filter.value

    if (id.value === null) {
      throw new Error('Evento não encontrado')
    }

    const [ overview ] = await Promise.all([
      new EventService().overview(id.value),
    ])

    data.value = overview.data.data

    latestFetchedEvent.value = id.value
  } catch (error) {
    snackbar.show('Você não foi possível buscar os eventos', 'error')

    throw error
  } finally {
    loader.decrement()
  }
}

// --- Signature Readings Management ---

// So we know wether we need to fetch data of the selected event before opening
// the dialog
const latestFetchedEvent = ref<string | null>(null)

const signatureReadingsDialog = ref(false)

async function openSignatureReadingsDialog() {
  if (latestFetchedEvent.value !== filter.value.resource?.id) {
    await fetch()
  }
  signatureReadingsDialog.value = true
}

function onSignatureReadingsDialogClose(readings: OverviewSignatureItem[]) {
  if (data.value?.signatures) {
    data.value.signatures = readings
  }
  signatureReadingsDialog.value = false
}

// --- Printing ---
const printingDialog = ref(false)

onMounted(() => {
  usePrintingStore().$reset()
  fetch()
})
</script>
